<template>
	<div class="mini-shop-page">
		<div class="page-head">
			<div class="tab-bar">
				<button
					v-for="item in tabs"
					:key="item.name"
					type="button"
					class="tab-btn"
					:class="{ 'is-active': tab === item.name }"
					@click="switchTab(item.name)"
				>
					{{ item.label }}
					<em v-if="badgeText(item.name) !== ''" class="tab-badge">{{ badgeText(item.name) }}</em>
				</button>
			</div>
			<el-form :inline="true" size="small" class="filter-form" @submit.native.prevent="onSearch">
				<el-form-item>
					<el-input
						v-model="keyword"
						clearable
						placeholder="商品名 / 款号 / 消费者昵称"
						class="keyword-input"
						@keyup.enter.native="onSearch"
					/>
				</el-form-item>
				<el-form-item>
					<el-select v-model="score" clearable placeholder="星级" class="score-select" @change="onSearch">
						<el-option v-for="n in 5" :key="n" :label="n + ' 星'" :value="n" />
					</el-select>
				</el-form-item>
				<el-form-item>
					<el-button type="primary" :loading="loading" @click="onSearch">查询</el-button>
					<el-button @click="onReset">重置</el-button>
					<el-button
						v-if="tab === 'pending' || tab === 'rejected'"
						type="primary"
						plain
						:disabled="!selected.length"
						:loading="batching"
						@click="onBatchApprove"
					>批量通过{{ selected.length ? '（' + selected.length + '）' : '' }}</el-button>
					<el-button
						v-if="tab === 'pending'"
						type="danger"
						plain
						:disabled="!selected.length"
						:loading="batching"
						@click="onBatchReject"
					>批量驳回{{ selected.length ? '（' + selected.length + '）' : '' }}</el-button>
				</el-form-item>
			</el-form>
		</div>

		<div class="table-wrap">
			<el-table
				:key="tab"
				:data="list"
				size="small"
				height="100%"
				v-loading="loading"
				class="shop-table"
				:row-class-name="rowClassName"
				@selection-change="onSelect"
			>
				<el-table-column v-if="canSelect" type="selection" width="48" align="center" />
				<el-table-column label="商品" min-width="220">
					<template slot-scope="scope">
						<div class="goods-cell">
							<div class="thumb">
								<img v-if="scope.row.product_pic" :src="scope.row.product_pic" alt="" width="48" height="48">
								<i v-else class="el-icon-picture-outline"></i>
							</div>
							<div class="goods-meta">
								<div class="goods-name" :title="scope.row.product_name">{{ scope.row.product_name || '-' }}</div>
								<div class="sub">{{ scope.row.product_style_number || '-' }}</div>
							</div>
						</div>
					</template>
				</el-table-column>
				<el-table-column label="消费者" min-width="140">
					<template slot-scope="scope">
						<div class="user-cell">
							<div class="avatar">
								<img v-if="scope.row.user_avatar" :src="scope.row.user_avatar" alt="" width="32" height="32">
								<i v-else class="el-icon-user"></i>
							</div>
							<span>{{ scope.row.user_nickname || '微信用户' }}</span>
						</div>
					</template>
				</el-table-column>
				<el-table-column label="尺码" width="70" align="center">
					<template slot-scope="scope">{{ scope.row.size || '-' }}</template>
				</el-table-column>
				<el-table-column label="评分" width="110" align="center">
					<template slot-scope="scope">
						<span class="stars">{{ starText(scope.row.score) }}</span>
					</template>
				</el-table-column>
				<el-table-column label="评价内容" min-width="180" show-overflow-tooltip>
					<template slot-scope="scope">
						<span v-if="scope.row.content">{{ scope.row.content }}</span>
						<span v-else class="sub">（仅打星）</span>
					</template>
				</el-table-column>
				<el-table-column label="状态" width="90" align="center">
					<template slot-scope="scope">{{ scope.row.state_text || '-' }}</template>
				</el-table-column>
				<el-table-column label="备注" min-width="120" show-overflow-tooltip>
					<template slot-scope="scope">
						<span v-if="scope.row.reject_reason" :class="{ 'warn-text': isReviewHint(scope.row) }">
							{{ scope.row.reject_reason }}
						</span>
						<span v-else class="sub">-</span>
					</template>
				</el-table-column>
				<el-table-column label="商家回复" min-width="120" show-overflow-tooltip>
					<template slot-scope="scope">{{ scope.row.reply || '-' }}</template>
				</el-table-column>
				<el-table-column label="提交时间" width="160" align="center">
					<template slot-scope="scope">{{ scope.row.create_time_str || '-' }}</template>
				</el-table-column>
				<el-table-column label="操作" width="160" align="center" fixed="right">
					<template slot-scope="scope">
						<template v-if="tab === 'pending' || Number(scope.row.state) === 0">
							<span class="cell-link" @click="onApprove([scope.row.id])">通过</span>
							<span class="cell-link cell-link-gap" @click="onReject([scope.row.id])">驳回</span>
						</template>
						<template v-else-if="tab === 'rejected' || Number(scope.row.state) === 2">
							<span class="cell-link" @click="onApprove([scope.row.id])">复核通过</span>
						</template>
						<template v-else-if="tab === 'approved' || Number(scope.row.state) === 1">
							<span class="cell-link" @click="openReply(scope.row)">回复</span>
							<span class="cell-link cell-link-gap" @click="onHide(scope.row)">隐藏</span>
						</template>
						<span v-else class="sub">-</span>
					</template>
				</el-table-column>
			</el-table>
		</div>

		<div class="pager">
			<el-pagination
				background
				layout="total, sizes, prev, pager, next"
				:total="total"
				:current-page="page"
				:page-size="limit"
				:page-sizes="pageSizes"
				@current-change="onPageChange"
				@size-change="onSizeChange"
			/>
		</div>

		<el-dialog title="商家回复" :visible.sync="replyVisible" width="520px" append-to-body @close="resetReply">
			<el-input
				v-model="replyText"
				type="textarea"
				:rows="5"
				maxlength="500"
				show-word-limit
				placeholder="回复内容 1-500 字"
			/>
			<span slot="footer">
				<el-button size="small" @click="replyVisible = false">取消</el-button>
				<el-button type="primary" size="small" :loading="replying" @click="submitReply">保存</el-button>
			</span>
		</el-dialog>
	</div>
</template>

<script>
	import {
		MINI_COMMENT_API,
		MINI_COMMENT_TABS,
		MINI_SHOP_PAGE_SIZES,
		canUseMiniprogram,
		formatMiniGoodsBadge,
		getApiErrorMessage,
	} from '@/constants/retailPlatform'

	export default {
		name: 'MiniprogramComments',
		inject: ['appdata'],
		data() {
			return {
				tabs: MINI_COMMENT_TABS,
				pageSizes: MINI_SHOP_PAGE_SIZES,
				tab: 'pending',
				keyword: '',
				score: '',
				page: 1,
				limit: 20,
				total: 0,
				counts: {},
				list: [],
				selected: [],
				loading: false,
				batching: false,
				replyVisible: false,
				replyId: 0,
				replyText: '',
				replying: false,
			}
		},
		computed: {
			canSelect() {
				return this.tab === 'pending' || this.tab === 'rejected'
			},
		},
		mounted() {
			this.loadList()
		},
		methods: {
			badgeText(name) {
				return formatMiniGoodsBadge(this.counts[name])
			},
			starText(score) {
				const n = Math.max(0, Math.min(5, Math.round(Number(score) || 0)))
				return '★'.repeat(n) + '☆'.repeat(5 - n)
			},
			isReviewHint(row) {
				return row && String(row.reject_reason || '').trim() === '机审建议复核'
			},
			rowClassName({ row }) {
				return this.isReviewHint(row) ? 'review-hint-row' : ''
			},
			guardPermission() {
				if (!canUseMiniprogram(this.appdata && this.appdata.myinfo)) {
					this.$message.warning('未开通小程序功能，请联系管理员')
					return false
				}
				return true
			},
			switchTab(name) {
				if (this.tab === name) {
					return
				}
				this.tab = name
				this.page = 1
				this.selected = []
				this.loadList()
			},
			onSearch() {
				this.page = 1
				this.loadList()
			},
			onReset() {
				this.keyword = ''
				this.score = ''
				this.page = 1
				this.loadList()
			},
			onPageChange(page) {
				this.page = page
				this.loadList()
			},
			onSizeChange(limit) {
				this.limit = limit
				this.page = 1
				this.loadList()
			},
			onSelect(rows) {
				this.selected = rows || []
			},
			loadList() {
				if (!this.guardPermission()) {
					this.list = []
					this.total = 0
					this.counts = {}
					return
				}
				const params = {
					tab: this.tab,
					keyword: String(this.keyword || '').trim(),
					page: this.page,
					limit: this.limit,
				}
				const score = Number(this.score)
				if (score >= 1 && score <= 5) {
					params.score = score
				}
				this.loading = true
				this.$axios.get(MINI_COMMENT_API.list, { params: params }).then((res) => {
					if (!res.data || res.data.code != 0) {
						this.list = []
						this.total = 0
						this.counts = {}
						this.$message.error(getApiErrorMessage(res, '获取评价列表失败'))
						return
					}
					const data = res.data.data || {}
					this.list = data.list || []
					this.total = Number(data.total) || 0
					this.counts = data.counts || {}
					this.$root.$emit('mini-comment-pending', Number(this.counts.pending) || 0)
				}).catch((err) => {
					console.log(err)
					this.list = []
					this.total = 0
					this.counts = {}
					this.$message.error('获取评价列表失败')
				}).finally(() => {
					this.loading = false
				})
			},
			guardBatchIds(ids) {
				if (!ids || !ids.length) {
					this.$message.warning('请先勾选评价')
					return false
				}
				if (ids.length > 50) {
					this.$message.warning('单次最多操作 50 条')
					return false
				}
				return true
			},
			onBatchApprove() {
				this.onApprove(this.selected.map((row) => row.id))
			},
			onBatchReject() {
				this.onReject(this.selected.map((row) => row.id))
			},
			onApprove(ids) {
				if (!this.guardPermission() || !this.guardBatchIds(ids) || this.batching) {
					return
				}
				const label = this.tab === 'rejected' ? '复核通过' : '通过'
				this.$confirm('确认' + label + '所选 ' + ids.length + ' 条评价？通过后 C 端即时可见。', label + '评价', {
					type: 'warning',
					confirmButtonText: label,
				}).then(() => {
					this.batching = true
					return this.$axios.post(MINI_COMMENT_API.audit, {
						ids: ids,
						action: 'approve',
					})
				}).then((res) => {
					if (!res) {
						return
					}
					if (!res.data || res.data.code != 0) {
						this.$message.error(getApiErrorMessage(res, label + '失败'))
						return
					}
					const updated = Number((res.data.data || {}).updated) || 0
					this.$message.success(label + '成功' + (updated ? '（' + updated + ' 条）' : ''))
					this.selected = []
					this.loadList()
				}).catch((err) => {
					if (err === 'cancel' || err === 'close') {
						return
					}
					console.log(err)
					this.$message.error(label + '失败')
				}).finally(() => {
					this.batching = false
				})
			},
			onReject(ids) {
				if (!this.guardPermission() || !this.guardBatchIds(ids) || this.batching) {
					return
				}
				this.$prompt('驳回原因（选填，≤100 字）', '驳回评价', {
					confirmButtonText: '驳回',
					cancelButtonText: '取消',
					inputPlaceholder: '可不填',
					inputValidator: (value) => {
						if (value && String(value).trim().length > 100) {
							return '原因最多 100 字'
						}
						return true
					},
				}).then(({ value }) => {
					this.batching = true
					return this.$axios.post(MINI_COMMENT_API.audit, {
						ids: ids,
						action: 'reject',
						reason: String(value || '').trim(),
					})
				}).then((res) => {
					if (!res) {
						return
					}
					if (!res.data || res.data.code != 0) {
						this.$message.error(getApiErrorMessage(res, '驳回失败'))
						return
					}
					const updated = Number((res.data.data || {}).updated) || 0
					this.$message.success('驳回成功' + (updated ? '（' + updated + ' 条）' : ''))
					this.selected = []
					this.loadList()
				}).catch((err) => {
					if (err === 'cancel' || err === 'close') {
						return
					}
					console.log(err)
					this.$message.error('驳回失败')
				}).finally(() => {
					this.batching = false
				})
			},
			openReply(row) {
				if (!row || !row.id) {
					return
				}
				this.replyId = row.id
				this.replyText = String(row.reply || '')
				this.replyVisible = true
			},
			resetReply() {
				this.replyId = 0
				this.replyText = ''
			},
			submitReply() {
				if (!this.guardPermission() || !this.replyId || this.replying) {
					return
				}
				const text = String(this.replyText || '').trim()
				if (!text || text.length > 500) {
					this.$message.warning('回复需在 1-500 字之间')
					return
				}
				this.replying = true
				this.$axios.post(MINI_COMMENT_API.reply, {
					id: this.replyId,
					reply: text,
				}).then((res) => {
					if (!res.data || res.data.code != 0) {
						this.$message.error(getApiErrorMessage(res, '回复失败'))
						return
					}
					this.$message.success('回复已保存')
					this.replyVisible = false
					this.loadList()
				}).catch((err) => {
					console.log(err)
					this.$message.error('回复失败')
				}).finally(() => {
					this.replying = false
				})
			},
			onHide(row) {
				if (!this.guardPermission() || !row || !row.id || this.batching) {
					return
				}
				this.$confirm('隐藏后 C 端立即不可见，商品评价数同步扣减。确认隐藏？', '隐藏评价', {
					type: 'warning',
					confirmButtonText: '隐藏',
				}).then(() => {
					return this.$prompt('隐藏原因（选填，≤100 字）', '填写原因', {
						confirmButtonText: '确认隐藏',
						cancelButtonText: '取消',
						inputPlaceholder: '可不填',
						inputValidator: (value) => {
							if (value && String(value).trim().length > 100) {
								return '原因最多 100 字'
							}
							return true
						},
					})
				}).then(({ value }) => {
					this.batching = true
					return this.$axios.post(MINI_COMMENT_API.hide, {
						id: row.id,
						reason: String(value || '').trim(),
					})
				}).then((res) => {
					if (!res) {
						return
					}
					if (!res.data || res.data.code != 0) {
						this.$message.error(getApiErrorMessage(res, '隐藏失败'))
						return
					}
					this.$message.success('已隐藏')
					this.loadList()
				}).catch((err) => {
					if (err === 'cancel' || err === 'close') {
						return
					}
					console.log(err)
					this.$message.error('隐藏失败')
				}).finally(() => {
					this.batching = false
				})
			},
		},
	}
</script>

<style scoped lang="less">
	.mini-shop-page {
		height: 100%;
		display: flex;
		flex-direction: column;
		background: #fff;
		padding: 12px 16px 8px;
		box-sizing: border-box;
		overflow: hidden;
	}

	.page-head {
		flex: none;
	}

	.tab-bar {
		display: flex;
		align-items: center;
		border-bottom: 1px solid #ebeef5;
		margin-bottom: 12px;
	}

	.tab-btn {
		position: relative;
		display: inline-flex;
		align-items: center;
		height: 40px;
		padding: 0 16px;
		border: 0;
		background: transparent;
		color: #606266;
		font-size: 14px;
		line-height: 1;
		cursor: pointer;
		outline: none;

		&.is-active {
			color: #ff4714;
			font-weight: 500;

			&::after {
				content: '';
				position: absolute;
				left: 16px;
				right: 16px;
				bottom: 0;
				height: 2px;
				background: #ff4714;
			}
		}
	}

	.tab-badge {
		display: inline-block;
		min-width: 18px;
		height: 18px;
		margin-left: 6px;
		padding: 0 5px;
		border-radius: 9px;
		background: #f56c6c;
		color: #fff;
		font-style: normal;
		font-size: 12px;
		line-height: 18px;
		text-align: center;
	}

	.filter-form {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		margin-bottom: 12px;

		/deep/ .el-form-item {
			margin: 0 8px 8px 0;
		}
	}

	.keyword-input {
		width: 240px;
	}

	.score-select {
		width: 120px;
	}

	.table-wrap {
		flex: 1;
		min-height: 0;
	}

	.shop-table {
		width: 100%;

		/deep/ .review-hint-row {
			background: #fdf6ec;
		}
	}

	.goods-cell,
	.user-cell {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.thumb,
	.avatar {
		flex: none;
		width: 48px;
		height: 48px;
		border-radius: 4px;
		overflow: hidden;
		background: #f5f7fa;
		display: flex;
		align-items: center;
		justify-content: center;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}

	.avatar {
		width: 32px;
		height: 32px;
		border-radius: 50%;
	}

	.goods-name {
		color: #303133;
		line-height: 1.4;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		max-width: 160px;
	}

	.sub {
		margin-top: 2px;
		font-size: 12px;
		color: #909399;
	}

	.stars {
		color: #ffb400;
		letter-spacing: 1px;
	}

	.warn-text {
		color: #e6a23c;
	}

	.cell-link {
		color: #ff4714;
		cursor: pointer;
		font-size: 13px;
	}

	.cell-link-gap {
		margin-left: 8px;
	}

	.pager {
		flex: none;
		padding: 10px 0 4px;
		text-align: right;
	}
</style>



// WEBPACK FOOTER //
// src/components/StoreConfig/miniprogram/comments.vue